import { api } from '../console/api.ts';
import { field, pageHead } from '../console/components.ts';
import { esc, icon, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { selectControl } from '../controls.ts';

/**
 * «Integración con G&T» (#215): how Nubi works with Seguros G&T, for the direction and the
 * administration. The environment (a simulation, never productive), how each operation is done
 * (G&T's API or a work order to its desk), the activity log of every call Nubi made to G&T (a
 * redacted summary: the reference, the outcome, the latency, the error's category and the
 * retries), with the notices G&T sent Nubi among them (#225: their type, reference, result and how
 * long applying took), and what is still to confirm with G&T: the simulator's «por confirmar»
 * parameters, linked to its manual.
 */

const CARRIER = 'gyt';

type Via = 'API' | 'FILE' | 'RULES' | 'MANUAL' | 'DESK' | 'MIXED';
type Overview = {
  carrier: string;
  name: string;
  environment: 'SANDBOX' | 'PRODUCTION';
  connection: { reachable: boolean; latencyMs: number | null } | null;
  operations: {
    operation: string;
    via: Via;
    transport: string | null;
    deskSlaHours: number | null;
  }[];
  pending: {
    parameters: { parameter: string; simulatorValue: string; ask: string }[];
    manualUrl: string | null;
    consoleUrl: string | null;
    available: boolean;
  } | null;
};
type Call = {
  kind: 'CALL';
  id: number;
  operation: string;
  reference: { kind: string; value: string } | null;
  productLine: string | null;
  outcome: 'OK' | 'ERROR';
  resultStatus: string | null;
  errorKind: string | null;
  httpStatus: number | null;
  carrierCode: string | null;
  attempts: number;
  retries: number;
  latencyMs: number;
  at: string;
};
/** A notice the carrier sent (applied, still to apply, resent or refused), as the API summarizes it. */
type Notice = {
  kind: 'NOTICE';
  type: string | null;
  reference: string | null;
  result: 'APPLIED' | 'PENDING' | 'DUPLICATE' | 'INVALID_SIGNATURE' | 'NO_SECRET';
  refusal: 'MISSING_SIGNATURE' | 'BAD_SIGNATURE' | 'STALE_SIGNATURE' | null;
  outcome: 'OK' | 'ERROR';
  latencyMs: number | null;
  at: string;
};
type Activity = Call | Notice;

/** The pack's operations as the agency names them. */
export const OPERATION: Record<string, string> = {
  quote: 'Cotizar',
  issue: 'Emitir',
  endorse: 'Endosar',
  renew: 'Renovar',
  cancel: 'Cancelar',
  installments: 'Consultar cobros',
  remit: 'Remesar',
  statement: 'Estado de cuenta de comisiones',
  claim: 'Avisar siniestros',
};
/** Another method of an operation (`issue.status`): what it is for. */
const FOLLOW_UP: Record<string, string> = {
  'issue.status': 'Seguimiento de la solicitud',
  'claim.status': 'Seguimiento del siniestro',
};
export const operationName = (op: string) => {
  if (FOLLOW_UP[op]) return FOLLOW_UP[op];
  const [base, method] = op.split('.');
  const name = OPERATION[base ?? ''] ?? base ?? op;
  return method ? `${name} (seguimiento)` : name;
};

const TRANSPORT: Record<string, string> = {
  'json-http': 'servicio REST',
  soap: 'servicio SOAP',
  email: 'correo',
  files: 'archivos',
};
const REFERENCE: Record<string, string> = {
  QUOTE: 'Cotización',
  APPLICATION: 'Solicitud',
  POLICY: 'Póliza',
  ENDORSEMENT: 'Endoso',
  CANCELLATION: 'Cancelación',
  REMITTANCE: 'Remesa',
  STATEMENT: 'Estado de cuenta',
};
/** The canonical status of an answer, in the agency's words. */
const RESULT: Record<string, string> = {
  ISSUED: 'emitida',
  PENDING: 'en revisión de la aseguradora',
  APPLIED: 'aplicado',
  REFERRED: 'referido',
  DECLINED: 'rechazado',
  CANCELLED: 'cancelada',
};
/** Why a call failed (CarrierError's kind), without the carrier's text. */
export const ERROR_KIND: Record<string, string> = {
  AUTH: 'Credenciales rechazadas',
  VALIDATION: 'Datos rechazados',
  NOT_FOUND: 'No encontrado',
  CONFLICT: 'Rechazo de la aseguradora',
  RATE_LIMITED: 'Demasiadas llamadas',
  UNAVAILABLE: 'Sin servicio o sin respuesta',
  PROTOCOL: 'Respuesta ilegible',
  IN_PROGRESS: 'Ya estaba en curso',
  INTERNAL: 'Error de Nubi',
};

/** The notices in the agency's words (as «Avisos de las aseguradoras» names them). */
const NOTICE: Record<string, string> = {
  ISSUANCE_ACCEPTED: 'Solicitud aceptada',
  ISSUANCE_REJECTED: 'Solicitud rechazada',
  INSTALLMENT_PAID: 'Requerimiento pagado',
  POLICY_EXPIRING: 'Póliza por vencer',
  POLICY_RENEWED: 'Póliza renovada',
  ENDORSEMENT_APPLIED: 'Endoso aplicado',
  ENDORSEMENT_DECLINED: 'Endoso rechazado',
  CLAIM_STATUS_CHANGED: 'Siniestro actualizado',
  CLAIM_PAYMENT: 'Pago de siniestro',
};
/** What became of a notice: its pill (text and tone) and what it means. */
const NOTICE_RESULT: Record<Notice['result'], { label: string; tone: string; detail: string }> = {
  APPLIED: { label: 'Aplicado', tone: 'ok', detail: 'Nubi consultó a la aseguradora' },
  PENDING: { label: 'Por aplicar', tone: 'warn', detail: 'Nubi lo aplica enseguida' },
  DUPLICATE: { label: 'Duplicado', tone: 'info', detail: 'ya lo había recibido' },
  INVALID_SIGNATURE: { label: 'Firma inválida', tone: 'bad', detail: 'no se aplicó' },
  NO_SECRET: { label: 'Sin secreto', tone: 'bad', detail: 'no se pudo comprobar' },
};
/** Why a signature was refused, without the carrier's text. */
const REFUSAL: Record<string, string> = {
  MISSING_SIGNATURE: 'Llegó sin firma',
  BAD_SIGNATURE: 'La firma no coincide',
  STALE_SIGNATURE: 'Firma vencida (repetición)',
};
/** The filter that takes only the notices, among the operations. */
const NOTICES = 'notices';

/** «G&T» from «Seguros G&T». */
const shortName = (name: string) =>
  name.replace(/^Seguros\s+/i, '').replace(/,?\s*S\.\s?A\.?$/i, '');

/** The activity filters the person chose (back to «todas» when the session ends). */
let filter = { operation: '', outcome: '' };
export function resetIntegration() {
  filter = { operation: '', outcome: '' };
}

const latency = (ms: number) => `${ms.toLocaleString('es-GT')} ms`;

function modeOf(o: Overview['operations'][number], carrier: string) {
  const desk = `Mesa de ${carrier}`;
  switch (o.via) {
    case 'API':
      return {
        label: 'API',
        how: `En línea, por el ${TRANSPORT[o.transport ?? ''] ?? 'servicio'} de ${carrier}.`,
      };
    case 'DESK':
      return {
        label: desk,
        how: `Orden de trabajo a la mesa de ${carrier}${o.deskSlaHours ? `, ${o.deskSlaHours} h acordadas` : ''}.`,
      };
    case 'MANUAL':
      return {
        label: desk,
        how: `${cap(TRANSPORT[o.transport ?? ''] ?? 'Correo')} a la mesa de ${carrier}.`,
      };
    case 'FILE':
      return { label: 'Archivo', how: `Por archivo intercambiado con ${carrier}.` };
    case 'RULES':
      return { label: 'Reglas', how: 'Con las reglas publicadas de la aseguradora (indicativo).' };
    default:
      return { label: 'Según el producto', how: 'API en unos productos y mesa en otros.' };
  }
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function operationsCard(o: Overview, carrier: string) {
  const rows = o.operations
    .map((op) => {
      const m = modeOf(op, carrier);
      const cls = op.via === 'API' ? 'ok' : op.via === 'MIXED' ? 'info' : 'DRAFT';
      return `<tr><td><strong>${esc(OPERATION[op.operation] ?? op.operation)}</strong></td>
        <td><span class="pill ${cls}" data-mode>${esc(m.label)}</span></td>
        <td class="muted">${esc(m.how)}</td></tr>`;
    })
    .join('');
  return `<section class="card" aria-labelledby="gyt-ops-title"><div class="card-head"><h2 id="gyt-ops-title">Operaciones con ${esc(carrier)}</h2></div>
    <div class="table-wrap" data-component="gyt-operations"><table class="table">
      <thead><tr><th scope="col">Operación</th><th scope="col">Modo</th><th scope="col">Cómo se hace</th></tr></thead>
      <tbody>${rows}</tbody></table></div></section>`;
}

function callRow(a: Call) {
  const ok = a.outcome === 'OK';
  const status = a.resultStatus ? RESULT[a.resultStatus] : null;
  const error = ok
    ? '—'
    : `${esc(ERROR_KIND[a.errorKind ?? ''] ?? 'Error')}${
        a.httpStatus || a.carrierCode
          ? `<small class="muted mono">${[a.httpStatus ? `HTTP ${a.httpStatus}` : '', a.carrierCode ?? ''].filter(Boolean).map(esc).join(' · ')}</small>`
          : ''
      }`;
  const tries =
    a.attempts === 0
      ? '<span title="Respondida desde el registro de Nubi, sin llamar otra vez">0</span>'
      : a.retries
        ? `${a.attempts} <small class="muted">(${a.retries} ${a.retries === 1 ? 'reintento' : 'reintentos'})</small>`
        : String(a.attempts);
  return `<tr data-activity="${a.id}">
    <td class="nowrap">${esc(when(a.at))}</td>
    <td data-op>${esc(operationName(a.operation))}</td>
    <td>${a.reference ? `<span class="muted small">${esc(REFERENCE[a.reference.kind] ?? a.reference.kind)}</span> <span class="mono">${esc(a.reference.value)}</span>` : '<span class="muted">—</span>'}</td>
    <td class="stack"><span class="pill ${ok ? 'ok' : 'bad'}" data-outcome>${ok ? 'Correcta' : 'Con error'}</span>${status ? `<small class="muted">${esc(status)}</small>` : ''}</td>
    <td class="num">${esc(latency(a.latencyMs))}</td>
    <td class="num">${tries}</td>
    <td class="stack">${error}</td></tr>`;
}

function noticeRow(n: Notice, carrier: string) {
  const r = NOTICE_RESULT[n.result];
  const error =
    n.result === 'INVALID_SIGNATURE'
      ? `${esc(REFUSAL[n.refusal ?? ''] ?? 'Firma rechazada')}<small class="muted">No es de ${esc(carrier)} hasta probarlo: no se aplicó</small>`
      : n.result === 'NO_SECRET'
        ? `Sin secreto compartido<small class="muted">Nubi no puede comprobar la firma; ${esc(carrier)} lo reintenta</small>`
        : '—';
  return `<tr data-notice-result="${esc(n.result)}">
    <td class="nowrap">${esc(when(n.at))}</td>
    <td class="stack"><span data-op>Aviso de ${esc(carrier)}</span><small class="muted">${esc(n.type ? (NOTICE[n.type] ?? 'Aviso') : 'Tipo sin comprobar')}</small></td>
    <td>${n.reference ? `<span class="mono">${esc(n.reference)}</span>` : '<span class="muted">—</span>'}</td>
    <td class="stack"><span class="pill ${r.tone}" data-outcome>${esc(r.label)}</span><small class="muted">${esc(r.detail)}</small></td>
    <td class="num">${n.latencyMs === null ? '<span class="muted">—</span>' : esc(latency(n.latencyMs))}</td>
    <td class="num"><span class="muted">—</span></td>
    <td class="stack">${error}</td></tr>`;
}

const activityRow = (a: Activity, carrier: string) =>
  a.kind === 'NOTICE' ? noticeRow(a, carrier) : callRow(a);

/** The newest calls the table shows (the API filters: older calls of an operation are found too). */
const SHOWN = 100;
const filtered = () => !!(filter.operation || filter.outcome);

function activityCard(shown: Activity[], any: boolean, operations: string[], carrier: string) {
  const filters = `<div class="toolbar" role="group" aria-label="Filtrar la actividad">
    ${field(
      'Operación',
      selectControl({ id: 'act-op' }, [
        { value: '', label: 'Todas', selected: !filter.operation },
        ...operations.map((op) => ({
          value: op,
          label: OPERATION[op] ?? op,
          selected: filter.operation === op,
        })),
        {
          value: NOTICES,
          label: `Avisos de ${carrier}`,
          selected: filter.operation === NOTICES,
        },
      ]),
    )}
    ${field(
      'Resultado',
      selectControl({ id: 'act-outcome' }, [
        { value: '', label: 'Todos', selected: !filter.outcome },
        { value: 'OK', label: 'Correcta', selected: filter.outcome === 'OK' },
        { value: 'ERROR', label: 'Con error', selected: filter.outcome === 'ERROR' },
      ]),
    )}
    <span class="grow"></span>
    <span class="muted small">${shown.length === SHOWN ? `Las ${SHOWN} más recientes` : `${shown.length} ${shown.length === 1 ? 'registro' : 'registros'}`}</span></div>`;
  const body = shown.length
    ? `<div class="table-wrap" data-component="gyt-activity"><table class="table">
        <thead><tr><th scope="col">Cuándo</th><th scope="col">Operación</th><th scope="col">Referencia</th><th scope="col">Resultado</th><th scope="col" class="num">Latencia</th><th scope="col" class="num">Intentos</th><th scope="col">Error</th></tr></thead>
        <tbody>${shown.map((a) => activityRow(a, carrier)).join('')}</tbody></table></div>`
    : `<div data-component="gyt-activity">${
        any
          ? `<div class="empty-inline" data-component="activity-empty">${icon('inbox')}<p>No hay llamadas ni avisos con ese filtro.</p><button type="button" class="btn sm" data-act-clear>Quitar filtros</button></div>`
          : `<div class="empty-inline" data-component="activity-empty">${icon('bolt')}<p>Todavía no hay llamadas ni avisos. Cotiza un auto o gastos médicos y aquí verás cada llamada a la aseguradora y cada aviso que te mande.</p></div>`
      }</div>`;
  return `<section class="card" aria-labelledby="gyt-activity-title"><div class="card-head"><h2 id="gyt-activity-title">Actividad reciente</h2></div>
    ${filters}${body}
    <p class="note info">${icon('shield')}<span>Cada llamada guarda solo la referencia de la aseguradora, el resultado, la latencia, los intentos y la categoría del error; cada aviso, su tipo, su referencia, su resultado y cuánto tardó Nubi en aplicarlo: nunca credenciales, el contenido ni datos personales. Un aviso rechazado no se aplica y solo lo ve la agencia del código de agente que dice. Se conservan las últimas 500 llamadas y 200 avisos rechazados o repetidos.</span></p></section>`;
}

function pendingCard(o: Overview, carrier: string) {
  const p = o.pending;
  if (!p) return '';
  const links = [
    p.manualUrl
      ? `<a class="btn sm" href="${esc(p.manualUrl)}" target="_blank" rel="noopener noreferrer">${icon('book')}Manual del simulador</a>`
      : '',
    p.consoleUrl
      ? `<a class="btn sm" href="${esc(p.consoleUrl)}" target="_blank" rel="noopener noreferrer">${icon('sliders')}Consola del simulador</a>`
      : '',
  ].join('');
  const body = !p.available
    ? `<p class="note bad">${icon('alert')}<span>No se pudo leer el manual del simulador. Revisa que el simulador esté en línea y vuelve a cargar la pantalla.</span></p>`
    : p.parameters.length
      ? `<div class="table-wrap" data-component="gyt-pending-table"><table class="table">
          <thead><tr><th scope="col">Parámetro</th><th scope="col">Valor del simulador</th><th scope="col">Qué pedir a ${esc(carrier)}</th></tr></thead>
          <tbody>${p.parameters
            .map(
              (x) =>
                `<tr><td class="mono small">${esc(x.parameter)}</td><td>${esc(x.simulatorValue)}</td><td>${esc(x.ask)}</td></tr>`,
            )
            .join('')}</tbody></table></div>`
      : `<p class="note info">${icon('ok')}<span>El simulador no tiene parámetros por confirmar.</span></p>`;
  return `<section class="card" aria-labelledby="gyt-pending-title" data-component="gyt-pending">
    <div class="card-head"><h2 id="gyt-pending-title">Lo que falta confirmar con ${esc(carrier)}</h2><span class="spacer"></span>${links}</div>
    <p class="card-pad muted small">${esc(carrier)} no publica estos valores: el simulador usa uno razonable del mercado («por confirmar») hasta que ${esc(carrier)} entregue su documentación.${p.manualUrl ? '' : ' El manual del simulador no está publicado en este ambiente.'}</p>
    ${body}</section>`;
}

export async function integracionGyt() {
  const query = new URLSearchParams({ limit: String(SHOWN) });
  if (filter.operation === NOTICES) query.set('kind', 'NOTICE');
  else if (filter.operation) query.set('operation', filter.operation);
  if (filter.outcome) query.set('outcome', filter.outcome);
  const [o, activity, chosen] = await Promise.all([
    api<Overview>('GET', `/v1/integrations/${CARRIER}`),
    // The figures are of the latest calls, whatever the filter.
    api<{ items: Activity[] }>('GET', `/v1/integrations/${CARRIER}/activity?limit=200`),
    filtered()
      ? api<{ items: Activity[] }>('GET', `/v1/integrations/${CARRIER}/activity?${query}`)
      : null,
  ]);
  const carrier = shortName(o.name);
  const all = activity.items;
  const calls = all.filter((a): a is Call => a.kind === 'CALL');
  const notices = all.filter((a): a is Notice => a.kind === 'NOTICE');
  const failed = calls.filter((a) => a.outcome === 'ERROR').length;
  const refused = notices.filter((n) => n.outcome === 'ERROR').length;
  const errors = failed + refused;
  const latencies = calls.map((a) => a.latencyMs).sort((a, b) => a - b);
  const median = latencies.length ? latencies[Math.floor(latencies.length / 2)] : null;
  const retried = calls.filter((a) => a.retries > 0).length;
  let html = pageHead(
    `Integración con ${carrier}`,
    `Cómo trabaja Nubi con ${esc(o.name)}: qué operaciones hace en línea y cuáles por su mesa, cada llamada y cada aviso recientes y lo que falta confirmar con la aseguradora.`,
  );
  if (o.environment === 'SANDBOX')
    html += `<div class="sandbox-notice" role="note" data-component="sandbox-notice">${icon('warn')}<div>
      <strong>Simulación (sandbox) — no productivo</strong>
      <p>Nubi habla con el simulador de ${esc(carrier)}, construido con lo que ${esc(carrier)} publica; lo que no publica son supuestos «por confirmar». Nada de lo que pasa aquí llega a ${esc(carrier)} ni emite pólizas reales.</p></div></div>`;
  const conn = o.connection;
  html += `<section class="kpis" aria-label="Estado de la integración">
    <div class="card kpi ${conn && !conn.reachable ? 'warn' : ''}"><span class="kpi-label">${icon('bolt')}Conexión</span><span class="kpi-value">${
      conn ? (conn.reachable ? 'En línea' : 'Sin respuesta') : '—'
    }</span><span class="kpi-foot">${
      conn?.reachable && conn.latencyMs !== null
        ? `responde en ${esc(latency(conn.latencyMs))}`
        : conn
          ? 'el simulador no respondió'
          : 'no se prueba en producción'
    }</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('inbox')}Llamadas recientes</span><span class="kpi-value">${calls.length}</span><span class="kpi-foot">${retried} con reintentos · ${notices.length} ${notices.length === 1 ? 'aviso' : 'avisos'} de ${esc(carrier)}</span></div>
    <div class="card kpi ${errors ? 'warn' : ''}"><span class="kpi-label">${icon('alert')}Con error</span><span class="kpi-value">${errors}</span><span class="kpi-foot">${
      errors
        ? `${failed} ${failed === 1 ? 'llamada' : 'llamadas'} · ${refused} ${refused === 1 ? 'aviso rechazado' : 'avisos rechazados'}`
        : 'todo correcto'
    }</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('trend')}Latencia mediana</span><span class="kpi-value">${median === null || median === undefined ? '—' : esc(latency(median))}</span><span class="kpi-foot">de las llamadas recientes</span></div>
  </section>`;
  html += operationsCard(o, carrier);
  html += activityCard(
    chosen ? chosen.items : all.slice(0, SHOWN),
    all.length > 0,
    o.operations.map((op) => op.operation),
    carrier,
  );
  html += pendingCard(o, carrier);
  return html;
}

const applyFilter = () => {
  const value = (id: string) =>
    String((document.getElementById(id) as HTMLSelectElement | null)?.value ?? '');
  filter = { operation: value('act-op'), outcome: value('act-outcome') };
  void render(current, { quiet: true });
};

export const actions: Actions = {
  click: {
    actClear: () => {
      resetIntegration();
      void render(current, { quiet: true });
    },
  },
  change: {
    'act-op': applyFilter,
    'act-outcome': applyFilter,
  },
};
